/**
 * iOS in standalone mode resolves `position: fixed; inset: 0` against a layout
 * viewport that is sometimes stale at launch and never re-lays-out, so the app
 * box ends short of the screen. Measuring innerHeight ourselves and pinning it
 * to --app-h is the reliable fix; 100dvh remains the fallback.
 */
export function trackViewportHeight(): () => void {
  const apply = (): void => {
    document.documentElement.style.setProperty('--app-h', `${window.innerHeight}px`);
  };
  apply();
  window.addEventListener('resize', apply);
  window.addEventListener('orientationchange', apply);
  window.addEventListener('pageshow', apply);
  document.addEventListener('visibilitychange', apply);
  const settle = window.setTimeout(apply, 300);
  return () => {
    window.removeEventListener('resize', apply);
    window.removeEventListener('orientationchange', apply);
    window.removeEventListener('pageshow', apply);
    document.removeEventListener('visibilitychange', apply);
    window.clearTimeout(settle);
  };
}

export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    ('standalone' in navigator && (navigator as { standalone?: boolean }).standalone === true)
  );
}

export type Platform = 'ios' | 'android' | 'desktop';
export function detectPlatform(): Platform {
  const ua = navigator.userAgent;
  // iPadOS 13+ reports as Macintosh; touch points are the reliable tell.
  if (/iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'desktop';
}
